/**
 * WeekChart Component
 *
 * Recharts-based chart for displaying demand vs supply data.
 * Shows supply as stacked bars (one colour per strain) and demand as a line.
 *
 * US-PE-006: Sales Timeline - Phase 2 Chart Components
 */

import { useMemo } from 'react'
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts'
import ChartTooltip from './ChartTooltip'
import {
  CHART_CONFIG,
  STRAIN_COLORS,
  getDayLabel,
  getDateNumber
} from './chart-config'

/**
 * Custom X-axis tick component
 * Renders day name on first line, date number on second line
 *
 * @param {Object} props - Recharts tick props
 * @param {number} props.x - X coordinate
 * @param {number} props.y - Y coordinate
 * @param {Object} props.payload - Tick payload containing value
 */
function CustomXAxisTick({ x, y, payload }) {
  // payload.value contains "Mon|29" format
  const [day, dateNum] = (payload.value || '').split('|')

  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={12}
        textAnchor="middle"
        fill={CHART_CONFIG.axis.fill}
        fontSize={CHART_CONFIG.axis.fontSize}
      >
        {day}
      </text>
      <text
        x={0}
        y={0}
        dy={26}
        textAnchor="middle"
        fill={CHART_CONFIG.axis.fill}
        fontSize={CHART_CONFIG.axis.fontSize - 1}
        opacity={0.7}
      >
        {dateNum}
      </text>
    </g>
  )
}

/**
 * Main chart component for Sales Timeline
 *
 * @param {Object} props
 * @param {Array} props.data - Daily aggregated data from useTimelineData
 *   Each item: { date, demandKg, supplyByStrain: {strainName: kg}, totalSupplyKg }
 * @param {Array} props.strains - Array of strain objects { id, name, color }
 * @param {boolean} [props.showAllStrains=true] - Show stacked bars for all strains
 * @param {string} [props.selectedStrainId] - ID of selected strain (for single strain view)
 * @param {string} [props.weekLabel] - Label for the week (e.g., "6 - 12 Jan 2026")
 * @param {number} [props.height=200] - Chart height in pixels
 * @param {string} [props.className] - Additional CSS classes
 */
export default function WeekChart({
  data = [],
  strains = [],
  showAllStrains = true,
  selectedStrainId = null,
  weekLabel,
  height = 200,
  className = ''
}) {
  // Transform data for Recharts format
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return []

    return data.map((item) => {
      const dayLabel = getDayLabel(item.date)
      const dateNum = getDateNumber(item.date)

      const point = {
        // Combine day and date for X-axis tick: "Mon|29"
        day: `${dayLabel}|${dateNum}`,
        date: item.date,
        demand: item.demandKg || 0,
        supply: item.totalSupplyKg || 0,
        // Include batch breakdown for tooltip
        batchesByStrain: item.batchesByStrain || {}
      }

      // Add strain-specific supply values for stacked bars
      if (showAllStrains && item.supplyByStrain) {
        // Map strain names to their supply values using strain ID as key
        strains.forEach(strain => {
          // The supplyByStrain uses strain name as key
          const supplyValue = item.supplyByStrain[strain.name] || 0
          point[`supply_${strain.id}`] = supplyValue
        })
      }

      return point
    })
  }, [data, strains, showAllStrains])

  // Get strains with data (for legend and bars)
  const strainsWithData = useMemo(() => {
    if (!showAllStrains) {
      const selectedStrain = strains.find(s => s.id === selectedStrainId)
      return selectedStrain ? [selectedStrain] : []
    }

    // Find strains that have any supply data
    return strains.filter(strain => {
      return data.some(item =>
        item.supplyByStrain && item.supplyByStrain[strain.name] > 0
      )
    })
  }, [strains, data, showAllStrains, selectedStrainId])

  // Calculate Y-axis domain
  const yDomain = useMemo(() => {
    if (chartData.length === 0) return [0, 10]

    let maxValue = 0

    chartData.forEach(point => {
      // Check demand
      maxValue = Math.max(maxValue, point.demand || 0)

      // Check supply (either total or sum of stacked)
      if (showAllStrains) {
        let totalSupply = 0
        strains.forEach(strain => {
          totalSupply += point[`supply_${strain.id}`] || 0
        })
        maxValue = Math.max(maxValue, totalSupply)
      } else {
        maxValue = Math.max(maxValue, point.supply || 0)
      }
    })

    // Add 10% padding and round up to nice number
    const padded = maxValue * 1.1
    const roundTo = padded > 100 ? 50 : padded > 50 ? 10 : 5
    const rounded = Math.ceil(padded / roundTo) * roundTo

    return [0, Math.max(rounded, 10)]
  }, [chartData, strains, showAllStrains])

  // Get single strain colour if in single strain view
  // Must be declared before early return to satisfy React hooks rules
  const singleStrainColor = useMemo(() => {
    if (showAllStrains) return null

    const selectedStrain = strains.find(s => s.id === selectedStrainId)
    return selectedStrain?.color || STRAIN_COLORS.default[0]
  }, [strains, selectedStrainId, showAllStrains])

  // Handle empty data
  if (!data || data.length === 0) {
    return (
      <div className={`w-full ${className}`}>
        {weekLabel && (
          <div className="text-sm font-medium text-slate-600 mb-2">
            {weekLabel}
          </div>
        )}
        <div
          className="flex items-center justify-center bg-slate-50 rounded-lg border border-dashed border-slate-200"
          style={{ height }}
        >
          <span className="text-sm text-slate-500">No data for this period</span>
        </div>
      </div>
    )
  }

  return (
    <div className={`w-full ${className}`}>
      {weekLabel && (
        <div className="text-sm font-medium text-slate-600 mb-2">
          {weekLabel}
        </div>
      )}

      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart
          data={chartData}
          margin={CHART_CONFIG.margin}
        >
          {/* Grid */}
          <CartesianGrid
            strokeDasharray={CHART_CONFIG.grid.strokeDasharray}
            stroke={CHART_CONFIG.grid.stroke}
            horizontal={CHART_CONFIG.grid.horizontal}
            vertical={CHART_CONFIG.grid.vertical}
          />

          {/* X-Axis: Days of week with date numbers */}
          <XAxis
            dataKey="day"
            tick={<CustomXAxisTick />}
            tickLine={CHART_CONFIG.axis.tickLine}
            axisLine={CHART_CONFIG.axis.axisLine}
            height={45}
          />

          {/* Y-Axis: Weight in kg */}
          <YAxis
            domain={yDomain}
            fontSize={CHART_CONFIG.axis.fontSize}
            fill={CHART_CONFIG.axis.fill}
            tickLine={CHART_CONFIG.axis.tickLine}
            axisLine={CHART_CONFIG.axis.axisLine}
            tickFormatter={(value) => `${value}`}
            width={35}
          />

          {/* Custom Tooltip */}
          <Tooltip
            content={
              <ChartTooltip
                strains={strainsWithData}
                showAllStrains={showAllStrains}
              />
            }
            cursor={{ fill: 'rgba(0, 0, 0, 0.05)' }}
            wrapperStyle={{ zIndex: 100 }}
          />

          {/* Supply Bars */}
          {showAllStrains ? (
            // Stacked bars for all strains
            strainsWithData.map((strain) => (
              <Bar
                key={strain.id}
                dataKey={`supply_${strain.id}`}
                stackId="supply"
                fill={strain.color}
                name={strain.name}
                radius={CHART_CONFIG.supplyBar.radius}
                maxBarSize={CHART_CONFIG.supplyBar.maxBarSize}
              />
            ))
          ) : (
            // Single bar for selected strain
            <Bar
              dataKey="supply"
              fill={singleStrainColor}
              radius={CHART_CONFIG.supplyBar.radius}
              maxBarSize={CHART_CONFIG.supplyBar.maxBarSize}
              name="Supply"
            />
          )}

          {/* Demand Line */}
          <Line
            type="monotone"
            dataKey="demand"
            stroke={CHART_CONFIG.demandLine.stroke}
            strokeWidth={CHART_CONFIG.demandLine.strokeWidth}
            dot={CHART_CONFIG.demandLine.dot}
            activeDot={CHART_CONFIG.demandLine.activeDot}
            name="Demand"
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  )
}

/**
 * Compact version of WeekChart for use in smaller containers
 */
export function WeekChartCompact(props) {
  return (
    <WeekChart
      {...props}
      height={props.height || 150}
    />
  )
}
